import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, NavLink, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import './axiosConfig'; // Import to set up interceptors
import './App.css';
import './shared.css';
import Login from './components/Login';
import ChangePassword from './components/ChangePassword';
import ForgotPassword from './components/ForgotPassword';
import ErrorModalHost, { showError } from './components/ErrorModal';
import MaintenanceModeHost from './components/MaintenanceMode';
import NewChangesModalHost from './components/NewChangesModal';
import {
  PermissionsProvider,
  expandPermissionsList,
  parseLocationSchedulePermission,
  parseDemandLocationPermission,
  parseItemLocationPermission,
  parseModifierLocationPermission,
  parseInvoiceLocationPermission,
  parseGiftCardLocationPermission
} from './hooks/usePermissions';
import {
  PermissionPreviewProvider,
  usePermissionPreview
} from './context/PermissionPreviewContext';
import PermissionPreviewBanner from './components/PermissionPreviewBanner';
import { REPORTS_PAGE_PERMISSIONS } from './constants/reportPermissions';
import { TIP_POOL_PERMISSIONS } from './constants/tipPool';
import { PRINTING_PAGE_PERMISSIONS } from './constants/printingPermissions';
import { activityFromLocation, logActivity } from './utils/activityLog';
// Eager: often the post-login home — lazy Suspense was flashing "Loading..." on every visit.
import TimeCards from './components/TimeCards';

const EmployeeList = lazy(() => import('./components/EmployeeList'));
const EmployeeDetail = lazy(() => import('./components/EmployeeDetail'));
const SquareImport = lazy(() => import('./components/SquareImport'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const Profile = lazy(() => import('./components/Profile'));
const HomePlaceholder = lazy(() => import('./components/HomePlaceholder'));
const GlobalAuditLog = lazy(() => import('./components/GlobalAuditLog'));
const Reporting = lazy(() => import('./components/Reporting'));
const Printing = lazy(() => import('./components/Printing'));
const SchedulePrefForm = lazy(() => import('./components/SchedulePrefForm'));
const Documents = lazy(() => import('./components/Documents'));
const Payroll = lazy(() => import('./components/Payroll'));
const CashTips = lazy(() => import('./components/CashTips'));
const TipPool = lazy(() => import('./components/TipPool'));
const Scheduling = lazy(() => import('./components/Scheduling'));
const SchedulePrefs = lazy(() => import('./components/SchedulePrefs'));
const TimeOff = lazy(() => import('./components/TimeOff'));
const Roles = lazy(() => import('./components/Roles'));
const Items = lazy(() => import('./components/Items'));
const Modifiers = lazy(() => import('./components/Modifiers'));
const Inventory = lazy(() => import('./components/Inventory'));
const Invoices = lazy(() => import('./components/Invoices'));
const InvoiceWeekAtAGlancePrint = lazy(() => import('./components/InvoiceWeekAtAGlancePrint'));
const GiftCards = lazy(() => import('./components/GiftCards'));
const Transactions = lazy(() => import('./components/Transactions'));
const Settings = lazy(() => import('./components/Settings'));

const samePermissions = (a = [], b = []) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((perm, i) => perm === sortedB[i]);
};

// Make showError and refreshPermissions available globally for axios interceptor
if (typeof window !== 'undefined') {
  window.__showError = showError;
}

// Protected Route component
function ProtectedRoute({ children, isAuthenticated }) {
  const location = useLocation();
  if (isAuthenticated) return children;
  const next = `${location.pathname}${location.search || ''}`;
  return (
    <Navigate
      to={`/login?next=${encodeURIComponent(next)}`}
      replace
    />
  );
}

function effectiveUserPermissions(user) {
  return expandPermissionsList(user?.permissions || []);
}

function userHasPermission(user, permissionName) {
  const userPermissions = effectiveUserPermissions(user);
  return userPermissions.includes('superuser') || userPermissions.includes(permissionName);
}

function userHasAnyScheduleView(user) {
  const userPermissions = effectiveUserPermissions(user);
  if (userPermissions.includes('superuser')) return true;
  return userPermissions.some((perm) => {
    const parsed = parseLocationSchedulePermission(perm);
    return parsed && parsed.action === 'view';
  });
}

function userHasAnyDemandView(user) {
  const userPermissions = effectiveUserPermissions(user);
  if (userPermissions.includes('superuser')) return true;
  // Temporary: leftover global grants until location sync migrates them.
  if (
    userPermissions.includes('view_schedule_demand') ||
    userPermissions.includes('edit_schedule_demand')
  ) {
    return true;
  }
  return userPermissions.some((perm) => {
    const parsed = parseDemandLocationPermission(perm);
    return parsed && parsed.action === 'view';
  });
}

function userCanViewSchedulePrefs(user) {
  return (
    userHasPermission(user, 'view_schedule_prefs_page') ||
    userHasAnyDemandView(user)
  );
}

function userHasAnyItemView(user) {
  const userPermissions = effectiveUserPermissions(user);
  if (userPermissions.includes('superuser')) return true;
  return userPermissions.some((perm) => {
    const parsed = parseItemLocationPermission(perm);
    return parsed && parsed.action === 'view';
  });
}

function userHasAnyModifierView(user) {
  const userPermissions = effectiveUserPermissions(user);
  if (userPermissions.includes('superuser')) return true;
  return userPermissions.some((perm) => {
    const parsed = parseModifierLocationPermission(perm);
    return parsed && parsed.action === 'view';
  });
}

/** Mirrors backend GET /api/employees canListBasic — roster access via HR, related features, or own linked record. */
function userCanAccessEmployeesList(user) {
  if (linkedEmployeeId(user)) return true;
  const userPermissions = effectiveUserPermissions(user);
  if (userPermissions.includes('superuser')) return true;
  if (
    userPermissions.includes('view_employees') ||
    userPermissions.includes('create_employees') ||
    userPermissions.includes('view_square') ||
    userPermissions.includes('view_timecards') ||
    userPermissions.includes('view_timecards_as_shift_lead') ||
    userPermissions.includes('view_time_off') ||
    userPermissions.includes('create_time_off') ||
    userPermissions.includes('edit_time_off') ||
    userPermissions.includes('delete_time_off') ||
    userPermissions.includes('view_time_off_requests') ||
    userPermissions.includes('approve_time_off_requests') ||
    userPermissions.includes('deny_time_off_requests') ||
    userPermissions.includes('view_roles') ||
    userPermissions.includes('view_roles_page') ||
    userPermissions.includes('assign_roles') ||
    userPermissions.includes('create_roles') ||
    userPermissions.includes('view_schedule_prefs') ||
    userPermissions.includes('view_schedule_prefs_page') ||
    userPermissions.includes('edit_schedule_prefs') ||
    userPermissions.includes('view_schedule_demand') ||
    userPermissions.includes('edit_schedule_demand') ||
    userPermissions.includes('view_printing') ||
    userPermissions.includes('view_documents') ||
    userPermissions.includes('upload_documents') ||
    userPermissions.includes('edit_documents') ||
    userPermissions.includes('delete_documents')
  ) {
    return true;
  }
  return userPermissions.some(
    (perm) => parseLocationSchedulePermission(perm) || parseDemandLocationPermission(perm)
  );
}

function linkedEmployeeId(user) {
  if (!user || user.employee_id == null || user.employee_id === '') return null;
  const id = Number(user.employee_id);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function userCanAccessUserManagement(user) {
  return (
    userHasPermission(user, 'view_users') ||
    userHasPermission(user, 'edit_own_permissions') ||
    userHasPermission(user, 'grant_permissions') ||
    userHasPermission(user, 'revoke_permissions') ||
    userHasPermission(user, 'manage_account_requests') ||
    userHasPermission(user, 'manage_employee_links')
  );
}

function userCanAccessReports(user) {
  return REPORTS_PAGE_PERMISSIONS.some((perm) => userHasPermission(user, perm));
}

function userCanAccessPrinting(user) {
  return userHasPermission(user, 'view_printing');
}

function userCanAccessDocuments(user) {
  return userHasPermission(user, 'view_documents');
}

function userCanAccessPayroll(user) {
  return userHasPermission(user, 'view_payroll');
}

function userCanAccessCashTips(user) {
  return userHasPermission(user, 'view_cash_tips');
}

function userCanAccessTipPool(user) {
  const userPermissions = effectiveUserPermissions(user);
  return (
    userPermissions.includes('superuser') ||
    TIP_POOL_PERMISSIONS.some((perm) => userPermissions.includes(perm))
  );
}

function userCanAccessInventory(user) {
  return userHasPermission(user, 'view_inventory');
}

function userCanAccessTransactions(user) {
  return userHasPermission(user, 'view_transactions');
}

function userHasAnyInvoiceView(user) {
  const perms = effectiveUserPermissions(user);
  if (perms.includes('superuser')) return true;
  return perms.some((name) => {
    const parsed = parseInvoiceLocationPermission(name);
    return parsed && parsed.action === 'view';
  });
}

function userHasAnyGiftCardView(user) {
  const perms = effectiveUserPermissions(user);
  if (perms.includes('superuser')) return true;
  return perms.some((name) => {
    const parsed = parseGiftCardLocationPermission(name);
    return parsed && parsed.action === 'view';
  });
}

function userCanAccessSettings(user) {
  // Section view/edit perms expand to include view_settings via prerequisites.
  return userHasPermission(user, 'view_settings');
}

/** First nav page the user can open. Null when only Profile (or nothing) is available. */
function getDefaultHomePath(user) {
  if (!user) return null;
  if (userCanAccessEmployeesList(user)) return '/';
  if (userHasPermission(user, 'view_roles_page')) return '/roles';
  if (userHasAnyScheduleView(user)) return '/scheduling';
  if (userCanViewSchedulePrefs(user)) return '/schedule-prefs';
  if (
    userHasPermission(user, 'view_timecards') ||
    userHasPermission(user, 'view_timecards_as_shift_lead')
  ) {
    return '/time-cards';
  }
  if (
    userHasPermission(user, 'view_time_off') ||
    userHasPermission(user, 'view_time_off_requests')
  ) {
    return '/time-off';
  }
  if (userCanAccessDocuments(user)) return '/documents';
  if (userCanAccessPayroll(user)) return '/payroll';
  if (userCanAccessCashTips(user)) return '/cash-tips';
  if (userCanAccessTipPool(user)) return '/tip-pool';
  if (userHasAnyItemView(user)) return '/items';
  if (userHasAnyModifierView(user)) return '/modifiers';
  if (userCanAccessInventory(user)) return '/inventory';
  if (userHasAnyInvoiceView(user)) return '/invoices';
  if (userHasAnyGiftCardView(user)) return '/gift-cards';
  if (userCanAccessTransactions(user)) return '/transactions';
  if (userCanAccessReports(user)) return '/reports/today';
  if (userCanAccessPrinting(user)) return '/printing';
  if (userCanAccessUserManagement(user)) return '/users';
  if (userHasPermission(user, 'view_global_audit_log')) return '/audit-log';
  if (userCanAccessSettings(user)) return '/settings';
  return null;
}

function PermissionRoute({
  children,
  user,
  requiredAny = [],
  requireScheduleView = false,
  requireSchedulePrefsView = false,
  requireItemView = false,
  requireModifierView = false,
  requireInvoiceView = false,
  requireGiftCardView = false
}) {
  if (
    !requiredAny.length &&
    !requireScheduleView &&
    !requireSchedulePrefsView &&
    !requireItemView &&
    !requireModifierView &&
    !requireInvoiceView &&
    !requireGiftCardView
  ) {
    return children;
  }
  const userPermissions = effectiveUserPermissions(user);
  const hasAccess =
    userPermissions.includes('superuser') ||
    requiredAny.some((perm) => userPermissions.includes(perm)) ||
    (requireScheduleView && userHasAnyScheduleView(user)) ||
    (requireSchedulePrefsView && userCanViewSchedulePrefs(user)) ||
    (requireItemView && userHasAnyItemView(user)) ||
    (requireModifierView && userHasAnyModifierView(user)) ||
    (requireInvoiceView && userHasAnyInvoiceView(user)) ||
    (requireGiftCardView && userHasAnyGiftCardView(user));
  const fallback = getDefaultHomePath(user) || '/';
  return hasAccess ? children : <Navigate to={fallback} replace />;
}

/** Clear a stuck body scroll lock left by a modal when navigating between pages. */
function ClearStuckBodyScroll() {
  const location = useLocation();
  useEffect(() => {
    const hasModal = document.querySelector('.modal-overlay');
    if (!hasModal && document.body.style.overflow === 'hidden') {
      document.body.style.overflow = '';
    }
  }, [location.pathname]);
  return null;
}

/** Run the page enter animation briefly, then drop it so modals aren't trapped under the header. */
function AppMain({ children }) {
  const location = useLocation();
  const [entering, setEntering] = useState(true);

  useEffect(() => {
    setEntering(true);
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setEntering(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setEntering(false), 400);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  return (
    <main className={`App-main${entering ? ' App-main--enter' : ''}`}>
      {children}
    </main>
  );
}

function titleForPath(pathname) {
  if (pathname.startsWith('/login')) return 'Login';
  if (pathname.startsWith('/forgot-password')) return 'Forgot Password';
  if (pathname.startsWith('/reset-password')) return 'Reset Password';
  if (pathname.startsWith('/settings')) return 'Settings';
  if (pathname.startsWith('/users')) return 'Users';
  if (pathname.startsWith('/audit-log')) return 'Audit Log';
  if (pathname.startsWith('/time-cards')) return 'Timecards';
  if (pathname.startsWith('/time-off')) return 'Time Off';
  if (pathname.startsWith('/roles')) return 'Roles';
  if (pathname.startsWith('/items')) return 'Items';
  if (pathname.startsWith('/modifiers')) return 'Modifiers';
  if (pathname.startsWith('/inventory')) return 'Inventory';
  if (/^\/invoices\/week-at-a-glance-/i.test(pathname)) {
    return pathname.split('/').pop() || 'Week at a glance';
  }
  if (pathname.startsWith('/invoices')) return 'Invoices';
  if (pathname.startsWith('/gift-cards')) return 'Gift cards';
  if (pathname.startsWith('/transactions')) return 'Transactions';
  if (pathname.startsWith('/scheduling')) return 'Scheduling';
  if (pathname.startsWith('/schedule-prefs')) return 'Schedule Prefs';
  if (pathname.startsWith('/reports') || pathname.startsWith('/reporting')) return 'Reports';
  if (pathname.startsWith('/printing')) return 'Printing';
  if (pathname.startsWith('/form/')) return 'Schedule Preferences';
  if (pathname.startsWith('/documents')) return 'Documents';
  if (pathname.startsWith('/payroll')) return 'Payroll';
  if (pathname.startsWith('/cash-tips')) return 'Cash Tips';
  if (pathname.startsWith('/tip-pool')) return 'Tip Pool';
  if (pathname.startsWith('/profile')) return 'Profile';
  if (pathname.startsWith('/square-import')) return 'Import';
  if (pathname.startsWith('/employee/')) return 'Employee';
  if (pathname === '/' || pathname === '/employees') return 'Employees';
  return 'Home';
}

/** Browser tab title: page name with brand. */
function DocumentTitle({ canAccessEmployeesList = true }) {
  const location = useLocation();
  useEffect(() => {
    const printSlug = location.pathname.match(
      /^\/invoices\/(week-at-a-glance-.+)$/i
    );
    if (printSlug) {
      // Exact slug = default Save as PDF filename
      document.title = printSlug[1];
      return;
    }
    const onHome = location.pathname === '/' || location.pathname === '/employees';
    const page =
      onHome && !canAccessEmployeesList ? 'Home' : titleForPath(location.pathname);
    document.title = `${page} · Briny Bagels`;
  }, [location.pathname, canAccessEmployeesList]);
  return null;
}

/** Keep old /reporting/:id bookmarks working. */
function LegacyReportingRedirect() {
  const { reportId } = useParams();
  return <Navigate to={reportId ? `/reports/${reportId}` : '/reports/today'} replace />;
}

/** Log page / employee / tab navigation to the audit log. */
function NavigationActivityTracker({ isAuthenticated }) {
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated) return;
    const activity = activityFromLocation(location.pathname, location.search);
    if (activity) logActivity(activity);
  }, [isAuthenticated, location.pathname, location.search]);

  return null;
}

function Header({ isAuthenticated, user, onLogout }) {
  const location = useLocation();
  const navRef = React.useRef(null);
  const canAccessEmployeesList = userCanAccessEmployeesList(user);
  const isEmployeesActive =
    location.pathname === '/' ||
    location.pathname === '/employees' ||
    location.pathname.startsWith('/employee/');

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const active = nav.querySelector('.header-link.active');
    if (active && typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  }, [location.pathname]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <header className="App-header">
      <div className="header-content">
        <nav className="header-nav" aria-label="Main" ref={navRef}>
          {canAccessEmployeesList && (
            <NavLink to="/" end className={() => `header-link${isEmployeesActive ? ' active' : ''}`}>
              Employees
            </NavLink>
          )}
          {userHasPermission(user, 'view_roles_page') && (
            <NavLink to="/roles" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Roles
            </NavLink>
          )}
          {userHasAnyScheduleView(user) && (
            <NavLink to="/scheduling" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Scheduling
            </NavLink>
          )}
          {userCanViewSchedulePrefs(user) && (
            <NavLink to="/schedule-prefs" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Schedule Prefs
            </NavLink>
          )}
          {(userHasPermission(user, 'view_timecards') ||
            userHasPermission(user, 'view_timecards_as_shift_lead')) && (
            <NavLink to="/time-cards" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Timecards
            </NavLink>
          )}
          {(userHasPermission(user, 'view_time_off') ||
            userHasPermission(user, 'view_time_off_requests')) && (
            <NavLink to="/time-off" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Time Off
            </NavLink>
          )}
          {userCanAccessDocuments(user) && (
            <NavLink to="/documents" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Documents
            </NavLink>
          )}
          {userCanAccessPayroll(user) && (
            <NavLink to="/payroll" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Payroll
            </NavLink>
          )}
          {userCanAccessCashTips(user) && (
            <NavLink to="/cash-tips" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Cash Tips
            </NavLink>
          )}
          {userCanAccessTipPool(user) && (
            <NavLink to="/tip-pool" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Tip Pool
            </NavLink>
          )}
          {userHasAnyItemView(user) && (
            <NavLink to="/items" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Items
            </NavLink>
          )}
          {userHasAnyModifierView(user) && (
            <NavLink to="/modifiers" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Modifiers
            </NavLink>
          )}
          {userCanAccessInventory(user) && (
            <NavLink to="/inventory" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Inventory
            </NavLink>
          )}
          {userHasAnyInvoiceView(user) && (
            <NavLink to="/invoices" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Invoices
            </NavLink>
          )}
          {userHasAnyGiftCardView(user) && (
            <NavLink to="/gift-cards" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Gift cards
            </NavLink>
          )}
          {userCanAccessTransactions(user) && (
            <NavLink to="/transactions" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Transactions
            </NavLink>
          )}
          {userCanAccessReports(user) && (
            <NavLink
              to="/reports/today"
              className={() =>
                `header-link${
                  location.pathname.startsWith('/reports') ||
                  location.pathname.startsWith('/reporting')
                    ? ' active'
                    : ''
                }`
              }
            >
              Reports
            </NavLink>
          )}
          {userCanAccessPrinting(user) && (
            <NavLink to="/printing" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Printing
            </NavLink>
          )}
          {userCanAccessUserManagement(user) && (
            <NavLink to="/users" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Users
            </NavLink>
          )}
          {userHasPermission(user, 'view_global_audit_log') && (
            <NavLink to="/audit-log" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Audit Log
            </NavLink>
          )}
          {userCanAccessSettings(user) && (
            <NavLink to="/settings" className={({ isActive }) => `header-link${isActive ? ' active' : ''}`}>
              Settings
            </NavLink>
          )}
        </nav>
        <div className="header-nav-account">
          <NavLink
            to="/profile"
            className={({ isActive }) => `header-username-btn${isActive ? ' active' : ''}`}
            title="Profile"
          >
            {user?.username}
          </NavLink>
          <button
            type="button"
            className="btn btn-header"
            onClick={onLogout}
          >
            Logout
          </button>
        </div>
      </div>
    </header>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);

  // Function to refresh permissions from server
  const refreshPermissions = useCallback(async () => {
    try {
      const response = await axios.get('/api/auth/refresh-permissions');
      const newPermissions = response.data.permissions || [];
      const nextEmployeeId =
        response.data.employee_id == null || response.data.employee_id === ''
          ? null
          : Number(response.data.employee_id);
      const nextEmail =
        typeof response.data.email === 'string' && response.data.email.trim()
          ? response.data.email.trim()
          : null;
      
      // Only update state when permissions / employee link / email actually changed
      setUser(prevUser => {
        if (!prevUser) return prevUser;
        const prevEmployeeId =
          prevUser.employee_id == null || prevUser.employee_id === ''
            ? null
            : Number(prevUser.employee_id);
        const prevEmail =
          typeof prevUser.email === 'string' && prevUser.email.trim()
            ? prevUser.email.trim()
            : null;
        const permsSame = samePermissions(prevUser.permissions, newPermissions);
        const employeeSame = prevEmployeeId === nextEmployeeId;
        const emailSame = prevEmail === nextEmail;
        if (permsSame && employeeSame && emailSame) {
          return prevUser;
        }
        localStorage.setItem('permissions', JSON.stringify(newPermissions));
        const nextUser = {
          ...prevUser,
          permissions: newPermissions,
          employee_id: nextEmployeeId,
          email: nextEmail
        };
        localStorage.setItem(
          'user',
          JSON.stringify({
            id: nextUser.id,
            username: nextUser.username,
            employee_id: nextUser.employee_id,
            email: nextUser.email
          })
        );
        return nextUser;
      });
      
      return newPermissions;
    } catch (error) {
      console.error('Error refreshing permissions:', error);
      const status = error.response?.status;
      const msg = error.response?.data?.error || '';
      const authFailed =
        status === 401 ||
        (status === 403 && (msg === 'Invalid token.' || msg.startsWith('Invalid or expired token')));
      if (authFailed) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
        localStorage.removeItem('mustChangePassword');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        setIsAuthenticated(false);
        setMustChangePassword(false);
      }
      return null;
    }
  }, []);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const permissionsData = localStorage.getItem('permissions');
    const mustChange = localStorage.getItem('mustChangePassword') === 'true';
    
    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        const permissions = permissionsData ? JSON.parse(permissionsData) : [];
        setUser({ ...user, permissions });
        setIsAuthenticated(true);
        setMustChangePassword(mustChange);
        
        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
        localStorage.removeItem('mustChangePassword');
      }
    }
    setLoading(false);
  }, []);

  // Refresh permissions when session starts (login) or user id changes
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      refreshPermissions();
    }
  }, [isAuthenticated, user?.id, refreshPermissions]);

  // Live permission updates via SSE (grant/revoke pushes immediately)
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return undefined;
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
      return undefined;
    }

    const token = localStorage.getItem('token');
    if (!token) return undefined;

    let eventSource = null;
    let retryTimer = null;
    let closed = false;
    let attempt = 0;

    const connect = () => {
      if (closed) return;
      const currentToken = localStorage.getItem('token');
      if (!currentToken) return;

      eventSource = new EventSource(
        `/api/auth/permission-events?token=${encodeURIComponent(currentToken)}`
      );

      eventSource.addEventListener('permissions-changed', () => {
        refreshPermissions();
      });

      eventSource.addEventListener('connected', () => {
        attempt = 0;
      });

      eventSource.onerror = () => {
        if (eventSource) {
          eventSource.close();
          eventSource = null;
        }
        if (closed) return;
        // Exponential backoff: 1s, 2s, 4s … capped at 30s
        const delay = Math.min(30000, 1000 * 2 ** attempt);
        attempt += 1;
        retryTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (eventSource) eventSource.close();
    };
  }, [isAuthenticated, user?.id, refreshPermissions]);

  // Backup: refresh when tab becomes visible / window focused
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const onVisibleOrFocus = () => {
      if (document.visibilityState === 'visible') {
        refreshPermissions();
      }
    };

    document.addEventListener('visibilitychange', onVisibleOrFocus);
    window.addEventListener('focus', onVisibleOrFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibleOrFocus);
      window.removeEventListener('focus', onVisibleOrFocus);
    };
  }, [isAuthenticated, refreshPermissions]);

  // Periodic permission refresh (60s) — safety net if SSE drops; only updates if perms changed
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    
    const interval = setInterval(() => {
      refreshPermissions();
    }, 60000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated, refreshPermissions]);

  // Expose refreshPermissions globally for axios interceptor
  useEffect(() => {
    if (typeof window !== 'undefined' && isAuthenticated) {
      window.__refreshPermissions = refreshPermissions;
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.__refreshPermissions;
      }
    };
  }, [isAuthenticated, refreshPermissions]);

  // Let axios interceptor flip forced-password-change UI without a full reload
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.__setMustChangePassword = (value) => {
      setMustChangePassword(!!value);
    };
    return () => {
      delete window.__setMustChangePassword;
    };
  }, []);

  const handleLogin = (userData, token, mustChange = false, permissions = []) => {
    setUser({ ...userData, permissions });
    setIsAuthenticated(true);
    setMustChangePassword(mustChange);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    // Store permissions in localStorage for persistence
    localStorage.setItem('permissions', JSON.stringify(permissions));
  };

  const handlePasswordChanged = () => {
    setMustChangePassword(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    localStorage.removeItem('mustChangePassword');
    localStorage.removeItem('timecards');
    sessionStorage.removeItem('permissionPreview');
    Object.keys(localStorage)
      .filter((key) => key.startsWith('timecards:'))
      .forEach((key) => localStorage.removeItem(key));
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
    setMustChangePassword(false);
  };

  if (loading) {
    return <div className="loading" role="status">Loading...</div>;
  }

  return (
    <Router>
      <PermissionPreviewProvider realPermissions={user?.permissions || []}>
        <AppShell
          isAuthenticated={isAuthenticated}
          user={user}
          mustChangePassword={mustChangePassword}
          onLogout={handleLogout}
          onLogin={handleLogin}
          onPasswordChanged={handlePasswordChanged}
        />
      </PermissionPreviewProvider>
    </Router>
  );
}

function AppShell({
  isAuthenticated,
  user,
  mustChangePassword,
  onLogout,
  onLogin,
  onPasswordChanged
}) {
  const {
    isPreviewing,
    previewPermissions,
    previewEmployeeId,
    previewOverridesEmployeeLink
  } = usePermissionPreview();

  const effectivePermissions = useMemo(
    () => (isPreviewing ? previewPermissions : (user?.permissions || [])),
    [isPreviewing, previewPermissions, user?.permissions]
  );

  const effectiveUser = useMemo(() => {
    if (!user) return null;
    const next = { ...user, permissions: effectivePermissions };
    if (previewOverridesEmployeeLink) {
      next.employee_id = previewEmployeeId;
    }
    return next;
  }, [
    user,
    effectivePermissions,
    previewOverridesEmployeeLink,
    previewEmployeeId
  ]);

  const canAccessEmployeesList = userCanAccessEmployeesList(effectiveUser);
  const defaultHomePath = getDefaultHomePath(effectiveUser);
  const authedHome = defaultHomePath || '/';

  const location = useLocation();
  const isPublicForm = location.pathname.startsWith('/form/');

  return (
    <div className={`App${isPublicForm ? ' App--public-form' : ''}`}>
      <ClearStuckBodyScroll />
      <DocumentTitle canAccessEmployeesList={canAccessEmployeesList} />
      <NavigationActivityTracker isAuthenticated={isAuthenticated} />
      {!isPublicForm && (
        <div className="app-chrome">
          <PermissionPreviewBanner />
          <Header
            isAuthenticated={isAuthenticated}
            user={effectiveUser}
            onLogout={onLogout}
          />
        </div>
      )}
      <ErrorModalHost />
      <MaintenanceModeHost />
      <NewChangesModalHost enabled={isAuthenticated && !mustChangePassword} />
      <AppMain>
        <PermissionsProvider permissions={effectivePermissions}>
          <Suspense fallback={<div className="loading" role="status">Loading...</div>}>
            <Routes>
              <Route path="/login" element={
                isAuthenticated ? <Navigate to={authedHome} replace /> : <Login onLogin={onLogin} />
              } />
              <Route path="/form/:token" element={<SchedulePrefForm />} />
              <Route path="/forgot-password" element={
                isAuthenticated ? <Navigate to={authedHome} replace /> : <ForgotPassword />
              } />
              <Route path="/reset-password" element={
                isAuthenticated ? <Navigate to={authedHome} replace /> : <ForgotPassword />
              } />
              <Route path="/" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    {canAccessEmployeesList ? (
                      <EmployeeList />
                    ) : defaultHomePath ? (
                      <Navigate to={defaultHomePath} replace />
                    ) : (
                      <HomePlaceholder />
                    )}
                  </ProtectedRoute>
                )
              } />
              <Route path="/employee/:id" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <EmployeeDetail />
                  </ProtectedRoute>
                )
              } />
              <Route path="/square-import" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requiredAny={['view_square']}>
                      <SquareImport />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/users" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute
                      user={effectiveUser}
                      requiredAny={[
                        'view_users',
                        'edit_own_permissions',
                        'grant_permissions',
                        'revoke_permissions',
                        'manage_account_requests',
                        'manage_employee_links'
                      ]}
                    >
                      <UserManagement />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/profile" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Profile />
                  </ProtectedRoute>
                )
              } />
              <Route path="/settings" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute
                      user={effectiveUser}
                      requiredAny={['view_settings']}
                    >
                      <Settings />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/audit-log" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requiredAny={['view_global_audit_log']}>
                      <GlobalAuditLog />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/time-cards" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute
                      user={effectiveUser}
                      requiredAny={[
                        'view_timecards',
                        'view_timecards_as_shift_lead',
                      ]}
                    >
                      <TimeCards />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/time-off" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute
                      user={effectiveUser}
                      requiredAny={['view_time_off', 'view_time_off_requests']}
                    >
                      <TimeOff />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/roles" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requiredAny={['view_roles_page']}>
                      <Roles />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/items" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requireItemView>
                      <Items />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/modifiers" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requireModifierView>
                      <Modifiers />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/inventory" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requiredAny={['view_inventory']}>
                      <Inventory />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/invoices" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requireInvoiceView>
                      <Invoices />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/invoices/:printSlug" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requireInvoiceView>
                      <InvoiceWeekAtAGlancePrint />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/gift-cards" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requireGiftCardView>
                      <GiftCards />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/transactions" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requiredAny={['view_transactions']}>
                      <Transactions />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route
                path="/reports"
                element={<Navigate to="/reports/today" replace />}
              />
              <Route
                path="/reports/:reportId"
                element={
                  mustChangePassword ? (
                    <ChangePassword onPasswordChanged={onPasswordChanged} />
                  ) : (
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <PermissionRoute user={effectiveUser} requiredAny={REPORTS_PAGE_PERMISSIONS}>
                        <Reporting />
                      </PermissionRoute>
                    </ProtectedRoute>
                  )
                }
              />
              <Route
                path="/reporting"
                element={<Navigate to="/reports/today" replace />}
              />
              <Route
                path="/reporting/:reportId"
                element={<LegacyReportingRedirect />}
              />
              <Route path="/printing" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requiredAny={PRINTING_PAGE_PERMISSIONS}>
                      <Printing />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/documents" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requiredAny={['view_documents']}>
                      <Documents />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/payroll" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requiredAny={['view_payroll']}>
                      <Payroll />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/cash-tips" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requiredAny={['view_cash_tips']}>
                      <CashTips />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/tip-pool" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requiredAny={TIP_POOL_PERMISSIONS}>
                      <TipPool />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/scheduling" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requireScheduleView>
                      <Scheduling />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
              <Route path="/schedule-prefs" element={
                mustChangePassword ? (
                  <ChangePassword onPasswordChanged={onPasswordChanged} />
                ) : (
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PermissionRoute user={effectiveUser} requireSchedulePrefsView>
                      <SchedulePrefs />
                    </PermissionRoute>
                  </ProtectedRoute>
                )
              } />
            </Routes>
          </Suspense>
        </PermissionsProvider>
      </AppMain>
    </div>
  );
}

export default App;

